import React, { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
    RaisedButton,
    Dialog,
    IconButton,
    DatePicker,
    Table,
    TableBody,
    TableHeader,
    TableHeaderColumn,
    TableRow,
    TableRowColumn,
    SelectField,
    MenuItem,
} from 'material-ui';
import { TextField } from 'redux-form-material-ui';
import RemoveCircle from 'material-ui/svg-icons/content/remove-circle-outline';
import NavigationClose from 'material-ui/svg-icons/navigation/close';
import Pagination from 'components/Libs/Pagination';
import _, { defaults } from 'lodash';
import { getServiceProvidersHistory } from 'services/regions/serviceProvider';
import { fromUTC, toUTC } from 'helpers/dates';
import styles from 'styles';

const dialogStyles = {
    contentStyle: { width: '76%', padding: '20px', maxWidth: 'unset' },
};

const statusesList = [
    { name: 'All', value: null },
    { name: 'Approved', value: ['approved'] },
    { name: 'Rejected', value: ['rejected'] },
];

const Row = ({ item }) => {
    const {
        newServiceProvider: {
            name,
            activities,
            address,
            PIC,
            phones,
            emails,
        },
        statusAt,
        status,
        statusBy: { email },
    } = item;

    return (
        <TableRow>
            <TableRowColumn style={{ ...styles.tableRowColumnCSS, width: '13%' }}>
                {name}
            </TableRowColumn>
            <TableRowColumn style={{ ...styles.tableRowColumnCSS, width: '13%' }}>
                {activities.join(', ')}
            </TableRowColumn>
            <TableRowColumn style={{ ...styles.tableRowColumnCSS, width: '13%' }}>
                {address}
            </TableRowColumn>
            <TableRowColumn style={{ ...styles.tableRowColumnCSS, width: '13%' }}>
                {PIC}
            </TableRowColumn>
            <TableRowColumn style={{ ...styles.tableRowColumnCSS, width: '13%' }}>
                {phones.map((phone, phoneIndex) => (
                    <div key={phoneIndex}>{`${phone.code} ${phone.phone}`}</div>
                ))}
            </TableRowColumn>
            <TableRowColumn style={{ ...styles.tableRowColumnCSS, width: '13%' }}>
                {emails.map((em, emIndex) => (
                    <div key={emIndex}>{em}</div>
                ))}
            </TableRowColumn>
            <TableRowColumn style={{ ...styles.tableRowColumnCSS, width: '21%' }}>
                <div className='service-providers-history__table-cell status-at'>
                    <div 
                        className='status' 
                        style={status === 'approved' ? { color: '#4D9E0E' } : { color: '#E34848' }}
                    >
                        {status}
                    </div>
                    <div>{fromUTC(statusAt).toLocaleString()}</div>
                </div>
                <div>
                    <div>{`by ${email}`}</div>
                </div>
            </TableRowColumn>
        </TableRow>
    );
};

const ServiceProvidersHistoryDialog = ({ open, toggle }) => {
    const defaultPagination = {
        page: 1,
        filters: {
            query: '',
            dateFrom: '',
            dateTo: '',
            statuses: null,
        },
        limit: 25,
    };

    const [state, setState] = useState({
        rowsPerPage: [25, 50, 100],
        pagination: defaults({}, defaultPagination),
        serviceProvidersHistory: [],
        total: 0,
    });

    const updateRows = useCallback(async (currentState = state) => {
        const portId = window.location.pathname.split('/').slice(-1)[0];
        const { page, limit, filters } = currentState.pagination;
        const res = await getServiceProvidersHistory(portId, { page, limit, filters });
        setState(prev => ({ ...prev, serviceProvidersHistory: res.data, total: res.total }));
    }, [state]);

    useEffect(() => {
        if (open) {
            updateRows();
        }
    }, [open]);

    const handleChangeSearch = useCallback((e, value) => {
        setState(prev => ({
            ...prev,
            pagination: {
                ...prev.pagination,
                page: 1,
                filters: {
                    ...prev.pagination.filters,
                    query: value,
                },
            },
        }));
    }, []);

    const handleSearch = useCallback(() => {
        updateRows();
    }, [updateRows]);

    const handleStartDateFilter = useCallback((e, date) => {
        setState((prev) => {
            const newState = {
                ...prev,
                pagination: {
                    ...prev.pagination,
                    page: 1,
                    filters: {
                        ...prev.pagination.filters,
                        dateFrom: toUTC(date),
                    },
                },
            };
            updateRows(newState);
            return newState;
        });
    }, [updateRows]);

    const handleEndDateFilter = useCallback((e, date) => {
        setState((prev) => {
            const newDate = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59));
            const newState = {
                ...prev,
                pagination: {
                    ...prev.pagination,
                    page: 1,
                    filters: {
                        ...prev.pagination.filters,
                        dateTo: newDate.toISOString(),
                    },
                },
            };
            updateRows(newState);
            return newState;
        });
    }, [updateRows]);

    const handleRemoveDate = useCallback((type) => {
        setState((prev) => {
            const newState = _.cloneDeep(prev);
            if (type === 'from') {
                newState.pagination.filters.dateFrom = '';
            } else if (type === 'to') {
                newState.pagination.filters.dateTo = '';
            }
            updateRows(newState);
            return newState;
        });
    }, [updateRows]);

    const handleChangePagination = useCallback(({ page, numberOfRows }) => {
        setState((prev) => {
            const newState = {
                ...prev,
                pagination: {
                    ...prev.pagination,
                    page,
                    limit: numberOfRows,
                },
            };
            updateRows(newState);
            return newState;
        });
    }, [updateRows]);

    const handleSelectStatus = useCallback((ev, i, value) => {
        setState((prev) => {
            const newState = {
                ...prev,
                pagination: {
                    ...prev.pagination,
                    page: 1,
                    filters: {
                        ...prev.pagination.filters,
                        statuses: value,
                    },
                },
            };
            updateRows(newState);
            return newState;
        });
    }, [updateRows]);

    const {
        pagination: {
            filters: {
                query, dateFrom, dateTo, statuses, 
            },
            page,
            limit,
        },
        rowsPerPage,
        serviceProvidersHistory,
        total,
    } = state;

    const minDate = new Date();
    const maxDate = new Date();
    minDate.setFullYear(minDate.getFullYear() - 1);
    maxDate.setFullYear(maxDate.getFullYear() + 1);

    return (
        <Dialog
            open={open}
            contentStyle={dialogStyles.contentStyle}
            onRequestClose={toggle}
            paperClassName='dialog-service-provider'
            bodyStyle={{ overflowY: 'auto' }}
            modal
        >
            <div className='title-wrapper'>
                <h2 className='title'>Service Provider History</h2>
                <IconButton onClick={toggle}>
                    <NavigationClose color="#B3B3B3" />
                </IconButton>
            </div>
            
            <div className='service-providers-history'>
                <div className='service-providers-history__header'>
                    <div className='service-providers-history__header-item'>
                        <TextField
                            value={query}
                            onChange={handleChangeSearch}
                            hintText='Search'
                            onKeyPress={(event) => {
                                if (event.charCode === 13) {
                                    event.preventDefault();
                                    handleSearch();
                                }
                            }}
                        />
                    </div>
                    <div className='service-providers-history__header-item'>
                        <RaisedButton
                            label='Search'
                            style={styles.RaisedButton}
                            primary
                            onClick={handleSearch}
                        />
                    </div>
                    <div className='service-providers-history__header-item'>
                        <SelectField
                            style={{ width: '100%' }}
                            fullWidth
                            autoWidth
                            value={statuses}
                            onChange={handleSelectStatus}
                        >
                            {statusesList.map((s, key) => (
                                <MenuItem 
                                    key={key} 
                                    style={styles.menuItemCSS} 
                                    value={s.value} 
                                    primaryText={s.name} 
                                />
                            ))}
                        </SelectField>
                    </div>
                    <div className='service-providers-history__header-item'>
                        <Pagination
                            total={total}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            numberOfRows={limit}
                            updateRows={handleChangePagination}
                        />
                    </div>
                </div>
                <div className='service-providers-history__filters'>
                    <div className='service-providers-history__filters-item'>
                        <DatePicker 
                            floatingLabelText='Filter start date:'
                            autoOk
                            minDate={minDate}
                            maxDate={maxDate}
                            textFieldStyle={{ width: '120px' }}
                            shouldDisableDate={dateSelected => dateSelected.getTime() > new Date(dateTo).getTime()}
                            onChange={handleStartDateFilter}
                            value={dateFrom ? fromUTC(dateFrom) : undefined}
                        />
                        <IconButton
                            style={{ margin: '25px 7px 0 -7px' }}
                            disabled={!dateFrom}
                            onClick={() => handleRemoveDate('from')}
                        >
                            <RemoveCircle color={dateFrom ? 'red' : 'black'} />
                        </IconButton>
                    </div>
                    <div className='service-providers-history__filters-item'>
                        <DatePicker
                            floatingLabelText='Filter end date:'
                            autoOk
                            minDate={minDate}
                            maxDate={maxDate}
                            textFieldStyle={{ width: '120px' }}
                            shouldDisableDate={dateSelected => dateSelected.getTime() < new Date(dateFrom).getTime()}
                            onChange={handleEndDateFilter}
                            value={dateTo ? fromUTC(dateTo) : undefined}
                        />
                        <IconButton
                            style={{ margin: '25px 0 0 -7px' }}
                            disabled={!dateTo}
                            onClick={() => handleRemoveDate('to')}
                        >
                            <RemoveCircle color={dateTo ? 'red' : 'black'} />
                        </IconButton>
                    </div>
                </div>
                <div className='table-body service-providers-history__table'>
                    <Table selectable={false}>
                        <TableHeader displaySelectAll={false} adjustForCheckbox={false}>
                            <TableRow>
                                <TableHeaderColumn style={{ ...styles.tableRowColumnCSS, width: '13%' }}>
                                    company
                                </TableHeaderColumn>
                                <TableHeaderColumn style={{ ...styles.tableRowColumnCSS, width: '13%' }}>
                                    activity
                                </TableHeaderColumn>
                                <TableHeaderColumn style={{ ...styles.tableRowColumnCSS, width: '13%' }}>
                                    address
                                </TableHeaderColumn>
                                <TableHeaderColumn style={{ ...styles.tableRowColumnCSS, width: '13%' }}>
                                    PIC
                                </TableHeaderColumn>
                                <TableHeaderColumn style={{ ...styles.tableRowColumnCSS, width: '13%' }}>
                                    phone
                                </TableHeaderColumn>
                                <TableHeaderColumn style={{ ...styles.tableRowColumnCSS, width: '13%' }}>
                                    email
                                </TableHeaderColumn>
                                <TableHeaderColumn style={{ ...styles.tableRowColumnCSS, width: '21%' }}>
                                    by User
                                </TableHeaderColumn>
                            </TableRow>
                        </TableHeader>
                        <TableBody displayRowCheckbox={false}>
                            {serviceProvidersHistory.map(item => 
                                item.newServiceProvider && (
                                    <Row
                                        key={item.newServiceProvider._id + item.statusAt}
                                        item={item}
                                    />
                                ))}
                        </TableBody>
                    </Table>
                </div>
            </div>
        </Dialog>
    );
};

ServiceProvidersHistoryDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    toggle: PropTypes.func.isRequired,
};

Row.propTypes = {
    item: PropTypes.shape({
        newServiceProvider: PropTypes.shape({
            name: PropTypes.string.isRequired,
            activities: PropTypes.arrayOf(PropTypes.string).isRequired,
            address: PropTypes.string.isRequired,
            PIC: PropTypes.string.isRequired,
            phones: PropTypes.arrayOf(PropTypes.shape({
                code: PropTypes.string.isRequired,
                phone: PropTypes.string.isRequired,
            })).isRequired,
            emails: PropTypes.arrayOf(PropTypes.string).isRequired,
            _id: PropTypes.string.isRequired,
        }).isRequired,
        statusAt: PropTypes.string.isRequired,
        status: PropTypes.string.isRequired,
        statusBy: PropTypes.shape({
            email: PropTypes.string.isRequired,
        }).isRequired,
    }).isRequired,
};

export default ServiceProvidersHistoryDialog;



// WEBPACK FOOTER //
// ./app/components/Regions/PortManagement/blocks/ServiceProviders/ServiceProvidersView/ServiceProvidersHistoryDialog.jsx